import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useAuth } from '../contexts/AuthContext';
import ToastContext from '../contexts/ToastContext';
import MarkerInfoCard from './MarkerInfoCard';
import { listEvents } from '../utils/eventsApi';
import { supabase } from '../supabase';
import { 
  createMarkerIcon, 
  createPulsingMarkerIcon, 
  filterMarkers, 
  DEFAULT_MAP_CENTER,
  MARKER_TYPES,
  MARKER_STATUSES,
  getSportColor
} from '../utils/mapUtils';
import { MdMyLocation, MdRefresh, MdSearchOff } from 'react-icons/md';
import AppIcon from './icons/AppIcon';
import '../styles/LiveMap.css';

// Custom marker styles
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

export default function LiveMap({ onMarkerClick, onEventSelect }) {
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [filters, setFilters] = useState({
    sport: 'all',
    status: 'all',
    type: 'all'
  });
  const [sports, setSports] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [displayedMarkers, setDisplayedMarkers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectingLocationId, setSelectingLocationId] = useState(null);
  const { currentUser } = useAuth();
  const toast = useContext(ToastContext);
  const mapRef = useRef(null);
  const updateIntervalRef = useRef(null);

  // Fetch map data
  const fetchMapData = useCallback(async () => {
    try {
      const events = await listEvents({ sportType: filters.sport !== 'all' ? filters.sport : undefined });
      const eventMarkers = events
        .filter(event => event.latitude !== null && event.longitude !== null)
        .map(event => ({
          id: event.id,
          type: 'event',
          title: event.sportType,
          sport: event.sportType,
          location: event.location,
          latitude: event.latitude,
          longitude: event.longitude,
          participantsCount: event.participants.length,
          maxParticipants: event.maxParticipants,
          date: event.date,
          startTime: event.startTime
        }));
      const mapResponse = { events: eventMarkers, matches: [], fields: [], players: [] };
      const filtered = filterMarkers(eventMarkers, filters);
      setMapData(mapResponse);
      setDisplayedMarkers(filtered);
    } catch (err) {
      console.error('Error fetching map data:', err);
      if (toast && typeof toast.showToast === 'function') {
        toast.showToast('Erreur lors du chargement des données', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [filters, toast]);

  // Fetch available sports
  const fetchSports = useCallback(async () => {
    try {
      const events = await listEvents();
      const eventSports = [...new Set(events.map(event => event.sportType).filter(Boolean))];
      setSports(['all', ...eventSports]);
    } catch (err) {
      console.error('Error fetching sports:', err);
    }
  }, []);

  // Get user location
  const getUserLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ latitude, longitude });
          
          if (currentUser && supabase) {
            supabase.from('profiles').update({ latitude, longitude, is_online: true, last_seen: new Date().toISOString() })
              .eq('id', currentUser.id || currentUser._id)
              .then(({ error }) => error && console.error('Error updating location:', error));
          }
        },
        (error) => {
          console.warn('Geolocation error:', error);
          // Fallback to default location
          setUserLocation({
            latitude: DEFAULT_MAP_CENTER.latitude,
            longitude: DEFAULT_MAP_CENTER.longitude
          });
        }
      );
    }
  }, [currentUser]);

  // Search for city/country
  const searchLocation = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setSearchLoading(true);
    try {
      console.log('🔍 Searching for:', query);
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&q=${encodeURIComponent(query)}`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Location search failed');
      const results = await response.json();
      console.log('📍 Setting results:', results);
      
      setSearchResults(Array.isArray(results) ? results : []);
      setShowSearchResults(true);
    } catch (err) {
      console.error('❌ Error searching location:', err);
      setSearchResults([]);
      if (toast && typeof toast.showToast === 'function') {
        toast.showToast('Erreur lors de la recherche', 'error');
      }
    } finally {
      setSearchLoading(false);
    }
  }, [toast]);

  // Handle location selection from search
  const handleSelectLocation = useCallback((location) => {
    const lat = parseFloat(location.lat);
    const lon = parseFloat(location.lon);
    
    // Add selection animation
    setSelectingLocationId(location.place_id);
    
    // Delay map transition for animation effect
    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.flyTo([lat, lon], 12, {
          duration: 1.5,
          easeLinearity: 0.25
        });
      }
      
      setSearchQuery('');
      setShowSearchResults(false);
      setSelectingLocationId(null);
    }, 300);
  }, []);

  // Initial load
  useEffect(() => {
    setLoading(true);
    fetchMapData();
    fetchSports();
    getUserLocation();
  }, []);

  // Refresh data on filter change
  useEffect(() => {
    setLoading(true);
    fetchMapData();
  }, [filters]);

  // Set up real-time updates (polling)
  useEffect(() => {
    updateIntervalRef.current = setInterval(() => {
      if (document.hidden) return; // Don't update if tab is hidden
      fetchMapData();
    }, 10000); // Update every 10 seconds

    return () => {
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
    };
  }, [fetchMapData]);

  const handleMapRefresh = () => {
    setLoading(true);
    fetchMapData();
  };

  const handleMarkerClick = (marker) => {
    setSelectedMarker(marker);
    if (onMarkerClick) onMarkerClick(marker);
  };

  const handleNavigateToDetails = (marker) => {
    if (marker.type === 'event' && onEventSelect) {
      onEventSelect(marker.id);
    }
  };

  return (
    <div className="premium-map-container">
      {/* Map occupies 85% */}
      <div className="premium-map-wrapper">
        {loading && <div className="map-loading">Chargement de la carte...</div>}
        
        <MapContainer
          center={[
            userLocation?.latitude || DEFAULT_MAP_CENTER.latitude,
            userLocation?.longitude || DEFAULT_MAP_CENTER.longitude
          ]}
          zoom={DEFAULT_MAP_CENTER.zoom}
          className="premium-map"
          ref={mapRef}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />

          {/* User location marker */}
          {userLocation && (
            <Marker 
              position={[userLocation.latitude, userLocation.longitude]}
              icon={L.icon({
                iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48Y2lyY2xlIGN4PSIxMiIgY3k9IjEyIiByPSI4IiBmaWxsPSIjM0I4MkY2Ii8+PC9zdmc+',
                iconSize: [16, 16],
                iconAnchor: [8, 8],
              })}
            >
              <Popup>Votre localisation</Popup>
            </Marker>
          )}

          {/* Display markers with smooth animation */}
          {displayedMarkers.map((marker, idx) => {
            const isPulsingMatch = marker.type === 'match' && marker.status === 'ongoing';
            const icon = isPulsingMatch 
              ? createPulsingMarkerIcon(marker.sport)
              : createMarkerIcon(marker.type, marker.sport);

            return (
              <Marker
                key={`${marker.type}-${marker.id}-${idx}`}
                position={[marker.latitude, marker.longitude]}
                icon={icon}
                eventHandlers={{
                  click: () => handleMarkerClick(marker)
                }}
                className="premium-marker"
              >
                <Popup className="map-popup">
                  <div className="popup-content">
                    <h4>{marker.title || marker.name}</h4>
                    <p>{marker.sport || marker.type}</p>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>

        {/* Search Location Bar */}
        <div className="location-search-bar">
          <div className="search-input-wrapper">
            <input
              type="text"
              placeholder="Rechercher une ville, un pays..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                searchLocation(e.target.value);
              }}
              onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
              onFocus={() => searchQuery && setShowSearchResults(true)}
              className="search-location-input"
            />
            {searchLoading && <span className="search-spinner">⟳</span>}
            
            {showSearchResults && searchResults.length > 0 && (
              <div className="search-results-dropdown">
                {searchResults.map((result, idx) => (
                  <div
                    key={idx}
                    className={`search-result-item ${selectingLocationId === result.place_id ? 'selecting' : ''}`}
                    onClick={() => handleSelectLocation(result)}
                  >
                    <span className="result-icon"><AppIcon name="location" size={18} /></span>
                    <div className="result-text">
                      <div className="result-name">{result.name}</div>
                      <div className="result-details">{result.address || ''}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Floating Filters Bar (glassmorphism) */}
        <div className="premium-filters-bar">
          <div className="filters-inner">
            <div className="filter-group-compact">
              <select 
                value={filters.type}
                onChange={(e) => setFilters({...filters, type: e.target.value})}
                className="filter-select-compact"
                title="Type de marqueur"
              >
                {MARKER_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.icon} {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group-compact">
              <select 
                value={filters.sport}
                onChange={(e) => setFilters({...filters, sport: e.target.value})}
                className="filter-select-compact"
                title="Sport"
              >
                <option value="all">Tous les sports</option>
                {sports.map(sport => (
                  <option key={sport} value={sport}>
                    {sport === 'all' ? 'Tous les sports' : sport}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group-compact">
              <select 
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                className="filter-select-compact"
                title="Statut"
              >
                {MARKER_STATUSES.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            <button 
              className="btn-refresh-floating"
              onClick={handleMapRefresh}
              disabled={loading}
              title="Actualiser la carte"
            >
              <MdRefresh className={loading ? 'spinning' : ''} />
            </button>
          </div>
        </div>

        {/* Legend - modern floating panel */}
        <div className="premium-legend-panel">
          <div className="legend-header">
            <span className="legend-title">Légende</span>
          </div>
          <div className="legend-items-premium">
            <div className="legend-item-premium">
              <span className="legend-icon"><AppIcon name="calendar" size={16} /></span>
              <span>Événements</span>
            </div>
            <div className="legend-item-premium">
              <span className="legend-icon"><AppIcon name="match" size={16} /></span>
              <span>Matchs en direct</span>
            </div>
            <div className="legend-item-premium">
              <span className="legend-icon"><AppIcon name="activity" size={16} /></span>
              <span>Terrains</span>
            </div>
            <div className="legend-item-premium">
              <span className="legend-icon"><AppIcon name="user" size={16} /></span>
              <span>Joueurs en ligne</span>
            </div>
          </div>
        </div>
      </div>

      {/* Info Card */}
      {selectedMarker && (
        <MarkerInfoCard
          marker={selectedMarker}
          onClose={() => setSelectedMarker(null)}
          onNavigate={handleNavigateToDetails}
        />
      )}

      {/* Empty state */}
      {!loading && displayedMarkers.length === 0 && (
        <div className="map-empty-state">
          <MdSearchOff />
          <p>Aucun marqueur trouvé pour ces filtres</p>
          <button onClick={() => setFilters({ sport: 'all', status: 'all', type: 'all' })}>
            Réinitialiser les filtres
          </button>
        </div>
      )}
    </div>
  );
}
